import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { DataContainer } from "../../components/DataContainer";
import { submitFeedback } from "../../store/knownDataSlice";
import TextareaAutosize from 'react-textarea-autosize';



function SendFeedback({ id }) {
    const dispatch = useDispatch();
    const data = useSelector((state) => state.knownData[id]);
    const user = useSelector((state) => state.user.data);

    // State for each driver's feedback: { driverID: { hours: [], wentWell: true, comment: "", feedbackID: null } }
    const [driverFeedbacks, setDriverFeedbacks] = useState({});

    // Get all drivers for this job (from existing feedbacks + primary driver)
    const getAllDrivers = () => {
        if (!data) return [];

        const drivers = [];
        const existingFeedbacks = data["job_feedbacks"] ? JSON.parse(data["job_feedbacks"]) : [];
        const processedDriverIds = new Set();

        // Add drivers who have provided feedback
        existingFeedbacks.forEach(feedback => {
            if (!processedDriverIds.has(feedback.driver_id)) {
                const isPrimary = feedback.driver_id === data["job_driver"];
                drivers.push({
                    id: feedback.driver_id,
                    name: isPrimary ?
                        `${data["driver_firstname"]} (peajuht)` :
                        `${feedback.driver_firstname} ${feedback.driver_lastname}`,
                    isPrimary: isPrimary
                });
                processedDriverIds.add(feedback.driver_id);
            }
        });

        // Always ensure primary driver is included even if no feedback yet
        if (data["job_driver"] && !processedDriverIds.has(data["job_driver"])) {
            drivers.push({
                id: data["job_driver"],
                name: `${data["driver_firstname"]} (peajuht)`,
                isPrimary: true
            });
            processedDriverIds.add(data["job_driver"]);
        }

        // Add extra drivers from the job if they exist and don't have feedback yet
        const extraDriverIds = data["job_extra_drivers"] ? JSON.parse(data["job_extra_drivers"]) : [];
        const extraDriverNames = data["extra_driver_names"] || [];

        extraDriverIds.forEach((driverId, index) => {
            if (!processedDriverIds.has(driverId)) {
                const name = extraDriverNames[index] || `Unknown Driver ${driverId}`;
                drivers.push({
                    id: driverId,
                    name: name,
                    isPrimary: false
                });
                processedDriverIds.add(driverId);
            }
        });

        return drivers;
    };

    const allDrivers = getAllDrivers();

    // Initialize feedback state from existing data or defaults
    useEffect(() => {
        if (!data || allDrivers.length === 0) return;

        const existingFeedbacks = data["job_feedbacks"] ? JSON.parse(data["job_feedbacks"]) : [];
        const feedbackState = {};

        allDrivers.forEach(driver => {
            const existingFeedback = existingFeedbacks.find(f => f.driver_id === driver.id);

            if (existingFeedback) {
                // Load existing feedback
                feedbackState[driver.id] = {
                    feedbackID: existingFeedback.feedback_id,
                    hours: Array.isArray(existingFeedback.hours) ? existingFeedback.hours : ["0"],
                    wentWell: existingFeedback.wentWell,
                    comment: existingFeedback.comment || "",
                    isRead: existingFeedback.isRead
                };
            } else {
                // Initialize empty feedback
                feedbackState[driver.id] = {
                    feedbackID: null,
                    hours: ["0"],
                    wentWell: true,
                    comment: "",
                    isRead: false
                };
            }
        });

        setDriverFeedbacks(feedbackState);
    }, [data, JSON.stringify(allDrivers)]);

    // Update a specific driver's feedback
    const updateDriverFeedback = (driverID, field, value) => {
        setDriverFeedbacks(prev => ({
            ...prev,
            [driverID]: {
                ...prev[driverID],
                [field]: value
            }
        }));
    };

    // Save feedback for a specific driver
    const saveFeedbackForDriver = async (driverID) => {
        const driverFeedback = driverFeedbacks[driverID];
        if (!driverFeedback) return;

        // Convert hours to proper format
        const processedHours = driverFeedback.hours.map(hour => {
            // Handle both string and number inputs
            if (typeof hour === 'number') {
                return hour;
            }
            const stringVal = String(hour);
            return parseFloat(stringVal.trim() === "" ? "0" : stringVal.trim().replace(",", "."));
        });

        try {
            const result = await dispatch(submitFeedback({
                id,
                driverId: driverID,
                wentWell: Boolean(driverFeedback.wentWell),
                comment: driverFeedback.comment,
                hours: JSON.stringify(processedHours),
            }));

            // Only show success message if the action succeeded
            if (result?.payload) {
                toast.success("Tagasiside salvestatud!");
            }
        } catch (error) {
            // Error is already handled by the Redux action with toast.error
            console.log("Feedback submission failed:", error);
        }
    };

    // Don't show if no job exists
    if(!data["job_query_id"]) return;

    return (
        <>
            <DataContainer actionType={"knownData/editDriverFeedback"}>
                <div className="column g32">
                    {allDrivers.length === 0 ? (
                        <p style={{ color: '#888', textAlign: 'center' }}>Ükski juht pole veel tagasisidet andnud</p>
                    ) : <></>}
                    {allDrivers.map(driver => {
                        const driverFeedback = driverFeedbacks[driver.id];
                        if (!driverFeedback) return null;

                        return (
                            <div key={driver.id} className="column g16" style={{
                                padding: '16px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                backgroundColor: driverFeedback.feedbackID ? '#f9f9f9' : '#fff'
                            }}>
                                <h3>
                                    {driver.name}
                                    {driverFeedback.feedbackID && (
                                        <span style={{ fontSize: '12px', color: '#666', marginLeft: '8px' }}>
                                            (Tagasiside olemas{driverFeedback.isRead ? ', loetud' : ', lugemata'})
                                        </span>
                                    )}
                                </h3>

                                {/* Hours input */}
                                <div className="column g8">
                                    <b>Töötunnid</b>
                                    <div className="column g8" style={{ marginLeft: '12px' }}>
                                        {driverFeedback.hours.map((val, index) => (
                                            <div key={index} className="row g8 center-cross">
                                                <b>{index + 1}. päev:</b>
                                                <input
                                                    style={{ width: '80px' }}
                                                    type="text"
                                                    value={val}
                                                    onChange={(e) => {
                                                        if(!e.target.value.match(/^(\d+[,\.]?\d*)$|^$/)) return;
                                                        const newHours = [...driverFeedback.hours];
                                                        newHours[index] = e.target.value;
                                                        updateDriverFeedback(driver.id, 'hours', newHours);
                                                    }}
                                                    onBlur={(e) => {
                                                        const newHours = [...driverFeedback.hours];
                                                        newHours[index] = parseFloat(e.target.value.trim() === "" ? "0" : e.target.value.trim().replace(",", ".")).toString();
                                                        updateDriverFeedback(driver.id, 'hours', newHours);
                                                    }}
                                                    onFocus={(e) => e.target.select()}
                                                />
                                                <p>tundi</p>
                                                {index > 0 && (
                                                    <button
                                                        onClick={() => {
                                                            const newHours = driverFeedback.hours.filter((_, i) => i !== index);
                                                            updateDriverFeedback(driver.id, 'hours', newHours);
                                                        }}
                                                        style={{ backgroundColor: '#d4635b', borderColor: '#f4837b', color: '#fff' }}
                                                    >×</button>
                                                )}
                                            </div>
                                        ))}
                                        <button onClick={() => {
                                            const newHours = [...driverFeedback.hours, "0"];
                                            updateDriverFeedback(driver.id, 'hours', newHours);
                                        }}>+ Lisa päev</button>
                                    </div>
                                </div>

                                {/* Feedback result */}
                                <div className="column g8">
                                    <b>Sõidu tulemus</b>
                                    <div className="column g8" style={{ marginLeft: '12px' }}>
                                        <div className="row g16">
                                            <input
                                                id={`jobWentWell-true-${driver.id}`}
                                                type="radio"
                                                name={`jobWentWell-${driver.id}`}
                                                checked={driverFeedback.wentWell}
                                                onChange={() => updateDriverFeedback(driver.id, 'wentWell', true)}
                                            />
                                            <label htmlFor={`jobWentWell-true-${driver.id}`}>Sõit läks plaanipäraselt</label>
                                        </div>
                                        <div className="row g16">
                                            <input
                                                id={`jobWentWell-false-${driver.id}`}
                                                type="radio"
                                                name={`jobWentWell-${driver.id}`}
                                                checked={!driverFeedback.wentWell}
                                                onChange={() => updateDriverFeedback(driver.id, 'wentWell', false)}
                                            />
                                            <label htmlFor={`jobWentWell-false-${driver.id}`}>Sõit ei läinud plaanipäraselt</label>
                                        </div>
                                    </div>
                                </div>

                                {/* Comment */}
                                <div className="column g8">
                                    <b>Kommentaar</b>
                                    <div style={{ marginLeft: '12px' }}>
                                        <TextareaAutosize
                                            spellCheck={false}
                                            minRows="2"
                                            style={{ resize: 'none', fontFamily: 'unset', width: '100%' }}
                                            placeholder='Kommentaar või märkused...'
                                            value={driverFeedback.comment}
                                            onChange={(e) => updateDriverFeedback(driver.id, 'comment', e.target.value)}
                                        />
                                    </div>
                                </div>

                                {/* Save button */}
                                <button onClick={() => saveFeedbackForDriver(driver.id)}>
                                    {driverFeedback.feedbackID ? 'Uuenda tagasiside' : 'Salvesta tagasiside'}
                                </button>
                            </div>
                        );
                    })}

                </div>
            </DataContainer>
        </>
    );
}

export default SendFeedback